import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { toast } from 'ngx-sonner';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProceduresService } from '../../../../core/services/procedures.service';
import { SupabaseService } from '../../../../core/services/supabase.service';
import { ClinicProcedure } from '../../../../core/models/procedure.model';
import { environment } from '../../../../../environments/environment';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'app-edit-procedure-modal',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    TranslateModule, 
    InputTextModule, 
    InputNumberModule, 
    SelectModule, 
    CheckboxModule
  ],
  template: `
    <div class="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" (click)="close.emit()"></div>

      <!-- Modal Content -->
      <div class="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col max-h-[90vh]">
        
        <!-- Header -->
        <div class="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <div>
            <h3 class="text-xl font-black text-slate-800 uppercase tracking-widest" id="modal-title">
              {{ 'modal_edit_procedure_title' | translate }}
            </h3>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{{ 'modal_edit_procedure_subtitle' | translate }}</p>
          </div>
          <button (click)="close.emit()" class="p-2 hover:bg-slate-50 rounded-xl transition-colors text-slate-400 hover:text-slate-600">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-4 custom-scrollbar">
          <!-- Toggle Active Status -->
          <div class="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100">
            <span class="text-[10px] font-black text-slate-800 uppercase tracking-widest">{{ 'modal_edit_procedure_active' | translate }}</span>
            <p-checkbox [(ngModel)]="isActive" [binary]="true"></p-checkbox>
          </div>

          <!-- Form Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_family' | translate }} <span class="text-red-500">*</span></label>
              <input pInputText type="text" [(ngModel)]="editableProcedure.family"
                     [invalid]="submitted && isMissing('family')"
                     class="w-full custom-input">
              <p *ngIf="submitted && isMissing('family')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'field_required' | translate }}
              </p>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_subfamily' | translate }} <span class="text-red-500">*</span></label>
              <input pInputText type="text" [(ngModel)]="editableProcedure.subfamily"
                     [invalid]="submitted && isMissing('subfamily')"
                     class="w-full custom-input">
              <p *ngIf="submitted && isMissing('subfamily')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'field_required' | translate }}
              </p>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_code' | translate }}</label>
              <input pInputText type="text" [(ngModel)]="editableProcedure.internal_code" 
                     class="w-full custom-input">
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_group' | translate }}</label>
              <input pInputText type="text" [(ngModel)]="editableProcedure.group" 
                     class="w-full custom-input">
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_subgroup' | translate }}</label>
              <input pInputText type="text" [(ngModel)]="editableProcedure.subgroup" 
                     class="w-full custom-input">
            </div>

            <div class="md:col-span-2">
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_description' | translate }} <span class="text-red-500">*</span></label>
              <input pInputText type="text" [(ngModel)]="editableProcedure.description"
                     [invalid]="submitted && isMissing('description')"
                     class="w-full custom-input">
              <p *ngIf="submitted && isMissing('description')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'field_required' | translate }}
              </p>
            </div>

            <div class="md:col-span-2">
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_type' | translate }}</label>
              <p-select [(ngModel)]="editableProcedure.procedure_type_id"
                        [options]="procedureTypes"
                        optionLabel="name"
                        optionValue="id"
                        [showClear]="true"
                        [placeholder]="'modal_edit_procedure_type_placeholder' | translate"
                        appendTo="body"
                        class="w-full"
                        [style]="{'width': '100%'}"></p-select>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_duration' | translate }} <span class="text-red-500">*</span></label>
              <p-inputNumber [(ngModel)]="editableProcedure.duration" [min]="0"
                             [invalid]="submitted && isMissing('duration')"
                             class="w-full" inputStyleClass="custom-input w-full"></p-inputNumber>
              <p *ngIf="submitted && isMissing('duration')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'field_required' | translate }}
              </p>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_value' | translate }} <span class="text-red-500">*</span></label>
              <p-inputNumber [(ngModel)]="editableProcedure.base_price"
                             mode="currency"
                             currency="EUR"
                             locale="pt-PT"
                             [invalid]="submitted && isMissing('base_price')"
                             class="w-full"
                             inputStyleClass="custom-input w-full"></p-inputNumber>
              <p *ngIf="submitted && isMissing('base_price')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'field_required' | translate }}
              </p>
            </div>

            <div class="md:col-span-2">
              <div class="flex items-center justify-between mb-1">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'modal_edit_procedure_iva' | translate }} <span class="text-red-500">*</span></label>
                <i *ngIf="isDropdownLoading" class="pi pi-spin pi-spinner text-[10px] text-primary"></i>
              </div>
              <p-select [(ngModel)]="editableProcedure.iva"
                        [options]="taxOptions"
                        optionLabel="displayLabel"
                        optionValue="id"
                        [placeholder]="'modal_edit_procedure_iva_placeholder' | translate"
                        (onChange)="onTaxChange()"
                        [invalid]="submitted && isMissing('iva')"
                        appendTo="body"
                        class="w-full"
                        [style]="{'width': '100%'}">
                <ng-template pTemplate="item" let-tax>
                   <div class="flex flex-col gap-0.5">
                      <span class="text-xs font-bold">{{ tax.label }}</span>
                      <span *ngIf="tax.region" class="text-[10px] text-slate-400">{{ tax.region }}</span>
                   </div>
                </ng-template>
              </p-select>
              <p *ngIf="submitted && isMissing('iva')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'vat_rate_required' | translate }}
              </p>
            </div>

            <div *ngIf="isExemptionEnabled()" class="md:col-span-2 animate-fadeIn">
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ 'modal_edit_procedure_exemption' | translate }} <span class="text-red-500">*</span></label>
              <p-select [(ngModel)]="editableProcedure.exemption_id" 
                        [options]="exemptions"
                        optionLabel="name" 
                        optionValue="id"
                        [placeholder]="'modal_edit_procedure_exemption_placeholder' | translate"
                        (onChange)="onExemptionChange()"
                        [invalid]="submitted && isMissing('exemption_id')"
                        appendTo="body"
                        class="w-full"
                        [style]="{'width': '100%'}">
                <ng-template pTemplate="item" let-ex>
                   <div class="flex flex-col gap-0.5">
                      <span class="text-xs font-bold">{{ ex.code }}</span>
                      <span class="text-[10px] text-slate-400">{{ ex.name }}</span>
                   </div>
                </ng-template>
              </p-select>
              <p *ngIf="submitted && isMissing('exemption_id')" class="text-[10px] text-red-500 font-bold uppercase tracking-widest mt-1">
                {{ 'billing_exemption_required_zero' | translate }}
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-slate-100 bg-white">
          <button 
            type="button"
            [disabled]="loading"
            (click)="save()"
            class="w-full py-3 bg-primary text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg shadow-primary/20 transform hover:-translate-y-0.5"
          >
            <svg *ngIf="loading" class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ (loading ? ('saving' | translate) : ('modal_edit_procedure_save' | translate)) }}
          </button>
        </div>
      </div>
    </div>
  `,
  // Each rule is written flat (`:host ::ng-deep .x`), never nested inside one
  // `:host ::ng-deep { ... }` block: the nested form compiles to an empty
  // `:is()` and the browser drops every rule in it. That is what had happened
  // here - none of these styles ever applied, so the selects showed PrimeNG's
  // default 16px text instead of the 13px semibold below.
  styles: [`
    :host ::ng-deep .p-select {
      height: 42px !important;
      display: flex !important;
      align-items: center !important;
      background: #f8fafc !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 12px !important;
      transition: all 0.2s !important;
    }
    :host ::ng-deep .p-select:hover {
      border-color: var(--primary-color) !important;
    }
    :host ::ng-deep .p-select .p-select-label {
      padding: 0 16px !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      color: #334155 !important;
    }
    :host ::ng-deep .custom-input {
      height: 42px !important;
      background: #f8fafc !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 12px !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      color: #334155 !important;
      padding: 0 16px !important;
      transition: all 0.2s !important;
    }
    :host ::ng-deep .custom-input:focus {
      border-color: var(--primary-color) !important;
      box-shadow: 0 0 0 4px rgba(var(--primary-color-rgb), 0.1) !important;
      outline: none !important;
    }
    :host ::ng-deep .p-inputnumber {
      width: 100%;
    }
    /* The rules above force a grey border with !important, which would hide
       PrimeNG's own red [invalid] border - so restate it, after them. */
    :host ::ng-deep .p-select.p-invalid,
    :host ::ng-deep .custom-input.p-invalid,
    :host ::ng-deep .p-inputnumber.p-invalid .custom-input {
      border-color: #ef4444 !important;
    }
  `]
})
export class EditProcedureModal implements OnInit {
  @Input() procedure: any;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  editableProcedure: any = {};
  isActive = true;
  loading = false;
  isDropdownLoading = false;
  /** Set by the first Save click; from then on missing fields show in red. */
  submitted = false;
  taxes: any[] = [];
  /**
   * The VAT select's options: the tax rows plus their region. Rate names repeat
   * per region ("Isento; 0%" exists for Portugal, Açores and Madeira), so
   * without it identical entries could not be told apart. Kept apart from
   * `taxes` because the chosen tax row is saved onto the procedure as tax_data.
   */
  taxOptions: { id: string; label: string; region: string; displayLabel: string }[] = [];
  exemptions: any[] = [];
  procedureTypes: any[] = [];
  private supabase = inject(SupabaseService);
  private store = inject(Store);
  private translate = inject(TranslateService);

  constructor(private proceduresService: ProceduresService) { }

  ngOnInit() {
    if (this.procedure) {
      // procedure_types_tb is an embedded join, not a real column — drop it
      // before this object gets sent back on save().
      const { procedure_types_tb, ...procedure } = this.procedure;
      this.editableProcedure = { ...procedure };
      this.isActive = !!this.editableProcedure.active;
      if (this.editableProcedure.iva) this.editableProcedure.iva = this.editableProcedure.iva.toString();
      if (this.editableProcedure.exemption_id) this.editableProcedure.exemption_id = this.editableProcedure.exemption_id.toString();
    }
    this.fetchDropdownData();
  }

  async fetchDropdownData() {
    this.isDropdownLoading = true;
    try {
      const userInfo: any = await firstValueFrom(
        this.store.select(selectUserInfo).pipe(
          filter((i) => !!i),
          take(1),
        ),
      );
      const branchId = userInfo.branches_id || userInfo.branch_id;

      if (!branchId) return;

      const [{ data, error }, { data: types, error: typesError }] = await Promise.all([
        // Usually already loaded by the Clinic tab's prefetch; fetched here if not.
        this.proceduresService.getTaxesAndExemptionsCached(branchId),
        this.proceduresService.getProcedureTypes(branchId),
      ]);

      if (error) throw error;
      if (typesError) throw typesError;

      this.procedureTypes = types || [];

      if (data) {
        // `taxes` is already narrowed to the branch's tax region (Settings >
        // Billing). A rate this procedure was saved with from ANOTHER region is
        // kept as an option, so the select is not blank and saving the
        // procedure unchanged does not silently drop its rate; the region
        // suffix on its label shows it is the odd one out.
        const regionTaxes: any[] = data.taxes || [];
        const iva = this.editableProcedure.iva;
        const saved = iva
          ? ((data.allTaxes || regionTaxes).find((t: any) => t.id == iva) ?? this.editableProcedure.tax_data)
          : null;
        this.taxes =
          saved && !regionTaxes.some((t: any) => t.id == saved.id) ? [...regionTaxes, saved] : regionTaxes;
        this.taxOptions = this.taxes.map((t: any) => {
          // "Açores (PT-AC)"; either half may be missing from the response.
          const name = t.region?.name || '';
          const code = t.region?.code || '';
          const region = name && code ? `${name} (${code})` : name || code;
          return {
            id: t.id,
            label: t.label,
            region,
            displayLabel: region ? `${t.label} · ${region}` : t.label,
          };
        });
        this.exemptions = data.exemptions || [];

        if (this.editableProcedure.iva) {
          this.onTaxChange();
        }
        if (this.editableProcedure.exemption_id) {
          this.onExemptionChange();
        }
      }
    } catch (e) {
      console.error('Failed to fetch dropdown data', e);
    } finally {
      this.isDropdownLoading = false;
    }
  }

  onTaxChange() {
    const selectedTax = this.taxes.find(t => t.id == this.editableProcedure.iva);
    if (selectedTax) {
      this.editableProcedure.tax_data = selectedTax;
    } else {
      this.editableProcedure.tax_data = null;
    }
    
    if (!this.isExemptionEnabled()) {
      this.editableProcedure.exemption_id = null;
      this.editableProcedure.exemption_data = null;
    }
  }

  onExemptionChange() {
    const selectedEx = this.exemptions.find(e => e.id == this.editableProcedure.exemption_id);
    if (selectedEx) {
      this.editableProcedure.exemption_data = selectedEx;
    } else {
      this.editableProcedure.exemption_data = null;
    }
  }

  isExemptionEnabled() {
    // Taxes come from an edge function. Until it answers (or if it fails), use
    // the rate stored on the procedure itself, so a 0% procedure cannot be
    // saved without an exemption just because the list had not loaded yet.
    const selectedTax =
      this.taxes.find(t => t.id == this.editableProcedure.iva) ?? this.editableProcedure.tax_data;
    return !!selectedTax && selectedTax.value == '0';
  }

  /** Every field marked * in the template. */
  private readonly requiredFields = [
    'family', 'subfamily', 'description', 'duration', 'base_price', 'iva', 'exemption_id',
  ];

  /**
   * Whether a required field is empty. Duration and value may legitimately be
   * 0, so for those only a blank counts. Exemption is required only while the
   * VAT rate is 0%: invoicing rejects a 0% line without one.
   */
  isMissing(field: string): boolean {
    const value = this.editableProcedure?.[field];
    switch (field) {
      case 'duration':
      case 'base_price':
        return value === null || value === undefined || value === '';
      case 'exemption_id':
        return this.isExemptionEnabled() && !value;
      default:
        return !String(value ?? '').trim();
    }
  }

  async save() {
    this.submitted = true;
    if (this.requiredFields.some((field) => this.isMissing(field))) {
      toast.error(this.translate.instant('fill_all_required_fields'));
      return;
    }

    this.loading = true;
    try {
      const userInfo: any = await firstValueFrom(
        this.store.select(selectUserInfo).pipe(
          filter((i) => !!i),
          take(1),
        ),
      );
      const updatedBy = userInfo?.name || userInfo?.fullName || 'Clinic User';

      const updates = {
        ...this.editableProcedure,
        active: this.isActive,
        updated_at: new Date().toISOString(),
        updated_by: updatedBy
      };

      const updateTasks = [
        this.proceduresService.updateClinicProcedure(this.procedure.id, updates)
      ];

      const masterId = this.procedure.procedure_id || this.procedure.product_id || this.procedure.id_procedure;
      if (masterId) {
        updateTasks.push(this.proceduresService.updateProcedure(masterId, { active: this.isActive }));
      }

      await Promise.all(updateTasks);
      toast.success(this.translate.instant('procedure_updated_success'));
      this.saved.emit();
      this.close.emit();
    } catch (error) {
      console.error('Failed to update procedure', error);
      toast.error(this.translate.instant('procedure_update_failed'));
    } finally {
      this.loading = false;
    }
  }
}
